// Фильтр технических сообщений об ошибках перед показом пользователю.
//
// В UI регулярно утекали строки двух видов: машинные коды (`NOT_FOUND`,
// `PREPARE_FAILED`) и низкоуровневые сетевые сообщения (`Failed to fetch`,
// `Load failed`, `NetworkError when attempting to fetch resource`). Первые
// ничего не значат для человека, вторые — особенно вредны офлайн: пользователь
// видит «ошибку», хотя произошло ровно ожидаемое отсутствие сети.
//
// Существовавший гвард ловил только `^[A-Z][A-Z0-9_]{2,}$` и все сетевые
// формулировки пропускал.
//
// Обратная сторона того же гварда: он глушит и ОСМЫСЛЕННЫЕ машинные коды.
// `FEATURE_TEMPORARILY_DISABLED` от killswitch до сих пор не объяснялся
// пользователю никак — сообщение просто исчезало, оставляя generic-текст. Тот
// же путь ждал `MODULE_UNAVAILABLE` от гейта модульной платформы. Поэтому
// известные коды теперь переводятся в человеческий текст через i18n
// (namespace `errors`), а не отбрасываются.

import {
	type ModuleUnavailableReason,
	parseModuleUnavailableMessage,
} from "@studyhub/shared/contracts/_shared";
import { toast } from "@studyhub/ui/toast";
import { isCommerceVisible } from "./module-availability/commerce";
import { presentedUnavailableReason } from "./module-availability/presented-reason";

const MACHINE_CODE = /^[A-Z][A-Z0-9_]{2,}$/;

const TECHNICAL_PATTERNS = [
	/failed to fetch/i,
	/load failed/i,
	/networkerror/i,
	/network request failed/i,
	/fetch failed/i,
	/^typeerror\b/i,
	/\bERR_[A-Z_]+\b/,
	/aborted/i,
	// Сообщения SDK синхронизации. В шапке печаталось буквальное
	// «powersync.write: HTTP 500 (group=tx_…)» — пользователю это не говорит
	// ничего, а сырой код нужен только телеметрии.
	/^powersync[.:]/i,
	/\bHTTP \d{3}\b/,
];

/** Минимальная форма `t` из react-i18next (чтобы lib не тянул i18next-типы). */
export type ErrorTranslator = (key: string, options?: Record<string, unknown>) => string;

/** Ключ namespace `errors` + подстановки для него. */
export interface KnownErrorCopy {
	key: string;
	values?: Record<string, string>;
}

/**
 * Известный машинный код → ключ человеческого текста.
 * `undefined` — код не наш, обрабатывается прежней логикой.
 */
export function knownErrorCopy(message: string | null | undefined): KnownErrorCopy | undefined {
	const raw = message?.trim() ?? "";
	if (!raw) return undefined;

	const moduleUnavailable = parseModuleUnavailableMessage(raw);
	if (moduleUnavailable) {
		return {
			// Причина решает, что показать: апселл, баннер аварии или «нет на
			// этой платформе» (§6.1 MODULE-PLATFORM-ARCHITECTURE).
			// В приложении из App Store «выше тарифа» звучит как «раздел выключен»
			// (guideline 3.1.1) — то же правило, что у экранов модуля.
			key: `errors:moduleUnavailable.${presentedUnavailableReason(
				moduleUnavailable.reason satisfies ModuleUnavailableReason,
				isCommerceVisible(),
			)}`,
			values: { module: moduleUnavailable.moduleId },
		};
	}
	// Человек запретил передачу данных AI-сервисам (`shared/ai-data-sharing` на
	// сервере). Код приходит двумя путями: tRPC-ошибкой из порта и причиной
	// закрытия сокета голоса — текст один.
	if (raw === "AI_DATA_SHARING_DENIED" || raw === "ai_data_sharing_denied") {
		return { key: "errors:aiDataSharingDenied" };
	}
	// Ops-killswitch: сервер шлёт голый код без деталей (`shared/killswitch.ts`).
	if (raw === "FEATURE_TEMPORARILY_DISABLED") {
		return { key: "errors:featureTemporarilyDisabled" };
	}
	return undefined;
}

/** Строку нельзя показывать пользователю как есть. */
export function isTechnicalErrorMessage(message: string | null | undefined): boolean {
	const raw = message?.trim() ?? "";
	if (!raw) return true;
	if (MACHINE_CODE.test(raw) && !raw.includes(" ")) return true;
	// Коды с разделителем (`MODULE_UNAVAILABLE:notes:killed`) под MACHINE_CODE не
	// подпадают — но сырыми их показывать тем более нельзя.
	if (knownErrorCopy(raw)) return true;
	return TECHNICAL_PATTERNS.some((re) => re.test(raw));
}

/**
 * Сообщение для пользователя или `undefined`, если исходное — техническое.
 * Вызывающий подставляет собственный человекочитаемый текст.
 *
 * `t` необязателен ради обратной совместимости, но без него известные коды
 * (`MODULE_UNAVAILABLE`, `FEATURE_TEMPORARILY_DISABLED`) снова превращаются в
 * `undefined` — передавай его везде, где `t` под рукой.
 */
export function userFacingErrorMessage(err: unknown, t?: ErrorTranslator): string | undefined {
	const raw = err instanceof Error ? err.message : typeof err === "string" ? err : "";
	const known = knownErrorCopy(raw);
	if (known && t) return t(known.key, known.values);
	return isTechnicalErrorMessage(raw) ? undefined : raw.trim();
}

/**
 * Toast ошибки без утечки технических сообщений (`Failed to fetch`, `NOT_FOUND`).
 * `fallback` — уже переведённая строка, обычно `t("…")`.
 */
export function toastError(err: unknown, fallback: string, t?: ErrorTranslator): void {
	toast.error(userFacingErrorMessage(err, t) ?? fallback);
}
